import type {
  Funder,
  Recommender,
  RoundAggregate,
} from "@engine/round/aggregate";
import {
  getActiveFunders,
  getFunderMatchingBudgetRatio,
} from "@engine/round/accessors";
import sum from "lodash/sum";
import type Matrix from "algorithm/matrix";
import {
  buildSyntheticRecommendersFromFunderAccounts,
  buildSyntheticRecommendersFromFunderAllocationSplit,
  type SyntheticRecommender,
} from "./syntheticRecommenders";
import { type RecommenderLayerInput } from "./recommenderPreferences";

/**
 * Budget portions bridge the funder layer and the recommender layer.
 *
 * The recommender layer normally treats each real recommender's budget as a
 * single allocation unit. That stops being sufficient when two funders send
 * money to the same recommender but exclude different proposals: the two pools
 * of money must follow the recommender's preferences with different proposal
 * eligibility. In that case these helpers split the real recommender into one
 * synthetic budget portion per effective exclusion set. `runRecommenderLayer`
 * allocates those portions independently, then combines their results under
 * the real recommender.
 *
 * Every portion carries two budgets because the recommender layer runs twice:
 * `standardBudget` is used to establish the allocation before matching, while
 * `budget` is the full amount used for the final matching-adjusted allocation.
 */

/**
 * One independently allocated portion of a real recommender's budget.
 *
 * A recommender normally has one portion. When its funders have different
 * proposal exclusions, synthetic recommenders split that budget into multiple
 * portions so each exclusion set can be allocated independently.
 */
export type RecommenderBudgetPortion = {
  id: string;
  recommender: Recommender;
  excludedProposalIds: string[];
  funderIds: string[];
  parentRecommenderIndex: number;
  budget: number;
  standardBudget: number;
};

// The discriminant tells the recommender layer whether the preference lists
// already built for real recommenders can be reused. Funder-specific exclusions
// require preference lists built for the synthetic recommenders instead.
export type RecommenderBudgetPortionSet =
  | {
      kind: "one-per-recommender";
      portions: RecommenderBudgetPortion[];
    }
  | {
      kind: "with-funder-specific-exclusions";
      portions: RecommenderBudgetPortion[];
    };

/**
 * Convert the synthetic-recommender representation used while grouping funder
 * dollars into the normalized rows consumed by `runRecommenderLayer`.
 *
 * Besides attaching the parent recommender and its output row index, this marks
 * whether any portion has an exclusion. The recommender layer uses that marker
 * to decide whether it can reuse the real recommenders' prebuilt preference
 * lists or must build exclusion-aware lists for the synthetic portions.
 */
function toRecommenderBudgetPortions(
  recommenderLayerInput: RecommenderLayerInput,
  synthetics: SyntheticRecommender[],
  getStandardBudget: (synthetic: SyntheticRecommender) => number,
): RecommenderBudgetPortionSet {
  const recommendersById = new Map(
    recommenderLayerInput.orderedRecommenders.map((recommender) => [
      recommender.id,
      recommender,
    ]),
  );

  const portions = synthetics.map((synthetic) => {
    const recommender = recommendersById.get(synthetic.recommenderId);
    const parentRecommenderIndexWithHold =
      recommenderLayerInput.recommenderIndicesMap[synthetic.recommenderId];
    if (recommender == null || parentRecommenderIndexWithHold == null) {
      throw new Error(
        `Missing parent recommender for synthetic recommender: ${synthetic.syntheticId}`,
      );
    }

    return {
      id: synthetic.syntheticId,
      recommender,
      excludedProposalIds: synthetic.excludedProposalIds,
      funderIds: synthetic.funderIds,
      budget: synthetic.budget,
      standardBudget: getStandardBudget(synthetic),
      // The index map includes hold at zero; account matrices do not have a
      // hold entry, so real recommender indices start at zero.
      parentRecommenderIndex: parentRecommenderIndexWithHold - 1,
    };
  });

  return {
    kind: synthetics.some(
      (synthetic) => synthetic.excludedProposalIds.length > 0,
    )
      ? "with-funder-specific-exclusions"
      : "one-per-recommender",
    portions,
  };
}

/**
 * Build recommender-layer budget portions from the actual funder-layer output.
 * This is the full-round allocation path: each matrix cell says how much of a
 * funder's budget reached a recommender, so those cells can be grouped by the
 * funders' effective proposal exclusions.
 *
 * The full `budget` of each portion comes from those grouped matrix cells. Its
 * `standardBudget` is calculated cell by cell using each contributing funder's
 * matching ratio; doing that before summing is necessary because funders in the
 * same portion can have different matching ratios.
 */
export function getBudgetPortionsFromFunderAccounts(
  round: RoundAggregate,
  funderAccounts: Matrix<"funder", "recommender+hold">,
  funders: Funder[],
  recommenderLayerInput: RecommenderLayerInput,
  minGrantExcludedProposalIds: { [proposalId: string]: number } = {},
): RecommenderBudgetPortionSet {
  const funderIndexById = new Map(
    funders.map((funder, funderIndex) => [funder.id, { funder, funderIndex }]),
  );
  return toRecommenderBudgetPortions(
    recommenderLayerInput,
    buildSyntheticRecommendersFromFunderAccounts(
      round,
      funderAccounts,
      funders,
      recommenderLayerInput.orderedRecommenders,
      recommenderLayerInput.recommenderIndicesMap,
      minGrantExcludedProposalIds,
    ),
    (synthetic) => {
      const recommenderCol =
        recommenderLayerInput.recommenderIndicesMap[synthetic.recommenderId];
      if (recommenderCol == null) {
        throw new Error(
          `Missing recommender index for synthetic: ${synthetic.syntheticId}`,
        );
      }
      return sum(
        synthetic.funderIds.map((funderId) => {
          const entry = funderIndexById.get(funderId);
          if (entry == null) {
            return 0;
          }
          return (
            funderAccounts.get(entry.funderIndex, recommenderCol) *
            (1 - getFunderMatchingBudgetRatio(round, entry.funder))
          );
        }),
      );
    },
  );
}

/**
 * Build budget portions from a caller-supplied attribution of each
 * recommender's budget back to its contributing funders.
 *
 * Incremental Evals and Funding Flow use this path because they run what-if
 * recommender allocations without rerunning the funder layer. The supplied
 * split stands in for the funder accounts matrix, allowing the same exclusion
 * grouping as the full-round path. If a recommender remains a single
 * non-excluded portion, its supplied standard budget is preserved exactly. If
 * it splits, the standard budget of each portion is reconstructed from its
 * funders so funder-specific matching ratios remain correctly attributed.
 */
export function getBudgetPortionsFromFunderAllocationSplit(
  round: RoundAggregate,
  recommenderLayerInput: RecommenderLayerInput,
  funderAllocationsByRecommender: {
    [recommenderId: string]: { [funderId: string]: number };
  },
  excludedByFunder: { [funderId: string]: string[] },
  recommenderBudgets: { [recommenderId: string]: number },
  standardRecommenderBudgets: { [recommenderId: string]: number },
): RecommenderBudgetPortionSet {
  const funderById = new Map(
    getActiveFunders(round).map((funder) => [funder.id, funder]),
  );
  const synthetics = buildSyntheticRecommendersFromFunderAllocationSplit(
    round,
    recommenderLayerInput.orderedRecommenders,
    funderAllocationsByRecommender,
    excludedByFunder,
    recommenderBudgets,
  );
  const portionCountByRecommender = new Map<string, number>();
  for (const synthetic of synthetics) {
    portionCountByRecommender.set(
      synthetic.recommenderId,
      (portionCountByRecommender.get(synthetic.recommenderId) ?? 0) + 1,
    );
  }
  return toRecommenderBudgetPortions(
    recommenderLayerInput,
    synthetics,
    (synthetic) => {
      if (
        portionCountByRecommender.get(synthetic.recommenderId) === 1 &&
        synthetic.excludedProposalIds.length === 0
      ) {
        return standardRecommenderBudgets[synthetic.recommenderId] ?? 0;
      }
      const split =
        funderAllocationsByRecommender[synthetic.recommenderId] ?? {};
      return sum(
        synthetic.funderIds.map((funderId) => {
          const funder = funderById.get(funderId);
          return funder == null
            ? 0
            : (split[funderId] ?? 0) *
                (1 - getFunderMatchingBudgetRatio(round, funder));
        }),
      );
    },
  );
}

/**
 * Build the identity representation: exactly one portion for every real
 * recommender, with no funder-specific exclusions.
 *
 * This is the fast path for callers that already have total and standard
 * budgets by recommender and do not need funder attribution. Because the rows
 * stay aligned with the real recommender order, `runRecommenderLayer` can reuse
 * the preference lists already present in `recommenderLayerInput`.
 */
export function getUnsplitBudgetPortions(
  recommenderLayerInput: RecommenderLayerInput,
  recommenderBudgets: { [recommenderId: string]: number },
  standardRecommenderBudgets: { [recommenderId: string]: number },
): RecommenderBudgetPortionSet {
  return {
    kind: "one-per-recommender",
    portions: recommenderLayerInput.orderedRecommenders.map(
      (recommender, parentRecommenderIndex) => ({
        id: recommender.id,
        recommender,
        excludedProposalIds: [],
        funderIds: [],
        budget: recommenderBudgets[recommender.id] ?? 0,
        standardBudget: standardRecommenderBudgets[recommender.id] ?? 0,
        parentRecommenderIndex,
      }),
    ),
  };
}
